// Da Vinci Block Component
import { DaVinciEvents } from "../core/DaVinciEvents.js";
import { DaVinciState } from "../core/DaVinciState.js";
import { DaVinciService } from "../DaVinciService.js";
import { DaVinciSanitizer } from "../utils/DaVinciSanitizer.js";

// Utility to create element from string
const htmlToElement = (html) => {
    const template = document.createElement('template');
    template.innerHTML = html.trim();
    return template.content.firstChild;
};

export const DaVinciBlock = {
    render(block) {
        if (block.type === 'text') return this.renderText(block);
        if (block.type === 'image') return this.renderImage(block);
        return document.createElement('div');
    },

    renderText(block) {
        // [Rich Text] Use Sanitizer to determine initial HTML
        // If legacy (no tags), convert to safe HTML (e.g. \n -> <br>)
        const initialHtml = DaVinciSanitizer.isHtml(block.content)
            ? DaVinciSanitizer.sanitize(block.content)
            : DaVinciSanitizer.convertLegacyToHtml(block.content);

        const template = `
            <div class="davinci-block" data-id="${block.id}" data-type="text">
                <span class="handle material-symbols-outlined">drag_indicator</span>
                <div class="btn-delete-block" data-action="delete" title="삭제">
                    <span class="material-symbols-outlined">delete</span>
                </div>
                <!-- ContentEditable for Rich Text -->
                <div class="dv-text-block rich-text" contenteditable="true" placeholder="내용을 입력하세요..." spellcheck="false">${initialHtml}</div>
            </div>
        `;
        const el = htmlToElement(template);

        // Bind Events Locally
        const editor = el.querySelector('.dv-text-block');

        // [Rich Text] Paste Handling (Sanitization)
        editor.addEventListener('paste', (e) => {
            e.preventDefault();
            // Get plain text first (simplest safe approach for Phase 1)
            // Future: Allow some HTML if copying from within editor
            const text = (e.originalEvent || e).clipboardData.getData('text/plain');

            // Insert text at cursor
            document.execCommand("insertText", false, text);
        });

        // Auto-expand is handled by CSS for contenteditable (usually min-height or height:auto)
        // But if we want to mimic textarea behavior, div does it naturally.

        editor.oninput = (e) => {
            // Sync innerHTML to State
            const val = e.target.innerHTML;
            DaVinciState.updateBlock(block.id, val);

            // [FIX] Character Limit Warning (Approximate by text length)
            const textLength = e.target.textContent.length;
            if (textLength > 600) {
                if (!editor.classList.contains('border-red-500')) {
                    editor.classList.add('border', 'border-red-500'); // Visual Cue
                    // Debounced Toast
                    if (!window._toastDebounce) {
                        window.Modal.toast("가독성을 위해 블록을 나눠주세요 (권장 600자 이내).");
                        window._toastDebounce = true;
                        setTimeout(() => window._toastDebounce = false, 3000);
                    }
                }
            } else {
                editor.classList.remove('border', 'border-red-500');
            }
        };

        editor.onkeydown = (e) => {
            // [Rich Text] Enter = default (creates <div> or <br>)
            // We might want to enforce <p> or <br> behavior later.
            // For now, browser default is fine.
        };

        // Initialize focus if it was the last modified block? 
        // (Optional state handling)

        this.bindCommon(el, block.id);
        return el;
    },

    renderImage(block) {
        const hasUrl = !!block.url;
        const contentHtml = hasUrl
            ? `<div class="dv-image-block"><img src="${block.url}" /></div>`
            : `<div class="dv-image-placeholder" data-action="upload">
                 <span class="material-symbols-outlined text-3xl">add_photo_alternate</span>
                 <div>이미지 업로드</div>
               </div>`;

        const template = `
            <div class="davinci-block" data-id="${block.id}" data-type="image">
                <span class="handle material-symbols-outlined">drag_indicator</span>
                <div class="btn-delete-block" data-action="delete" title="삭제">
                    <span class="material-symbols-outlined">delete</span>
                </div>
                ${contentHtml}
                ${block.isUploading ? `
                <div class="absolute inset-0 bg-black/60 flex flex-col items-center justify-center z-10 backdrop-blur-sm animate-fade-in">
                    <div class="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mb-2"></div>
                    <span class="text-xs text-indigo-300 font-bold tracking-wider">UPLOADING...</span>
                </div>` : ''}
            </div>
        `;
        const el = htmlToElement(template);

        if (!hasUrl) {
            const placeholder = el.querySelector('.dv-image-placeholder');

            // [FIX] Click Upload
            placeholder.onclick = () => {
                const input = document.createElement('input');
                input.type = 'file'; input.accept = 'image/*';
                input.onchange = async (e) => {
                    if (e.target.files[0]) {
                        DaVinciEvents.emit('block:request-upload', { id: block.id, file: e.target.files[0] });
                    }
                };
                input.click();
            };

            // [FIX] Drag & Drop
            ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
                placeholder.addEventListener(eventName, (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                }, false);
            });

            ['dragenter', 'dragover'].forEach(eventName => {
                placeholder.addEventListener(eventName, () => {
                    placeholder.style.borderColor = '#ffb74d'; // Accent Color
                    placeholder.style.backgroundColor = 'rgba(255, 183, 77, 0.1)';
                }, false);
            });

            ['dragleave', 'drop'].forEach(eventName => {
                placeholder.addEventListener(eventName, () => {
                    placeholder.style.borderColor = '';
                    placeholder.style.backgroundColor = '';
                }, false);
            });

            placeholder.addEventListener('drop', (e) => {
                const dt = e.dataTransfer;
                const file = dt.files[0];
                if (file && file.type.startsWith('image/')) {
                    DaVinciEvents.emit('block:request-upload', { id: block.id, file: file });
                }
            }, false);
        }

        this.bindCommon(el, block.id);
        return el;
    },

    bindCommon(el, id) {
        // Delete Button
        const deleteBtn = el.querySelector('.btn-delete-block');
        if (deleteBtn) {
            deleteBtn.onclick = (e) => {
                e.stopPropagation();
                // We emit intent, State handles confirmation/deletion
                DaVinciState.deleteBlock(id);
            };
        }
    }
};
